// Outils « niveau-nœud » du simulateur : crictl, kubeadm, openssl, systemctl /
// service, etcdctl / etcdutl. Objectif : donner le ressenti d'un VRAI serveur
// Linux d'administration Kubernetes (comme sur l'examen CKA/CKS), au-delà de
// kubectl. Ces binaires lisent l'état du cluster et le VFS (fichiers), et
// produisent des sorties crédibles. Ils ne simulent pas la mécanique réelle
// (containerd, PKI…) mais son OBSERVABLE.

import type { ClusterState, NodeObj } from './engine';
import type { LinuxShell } from './shell';

export interface ExecResultLite { text: string; isError: boolean }
const ok = (text = ''): ExecResultLite => ({ text, isError: false });
const err = (text: string): ExecResultLite => ({ text, isError: true });

export interface NodeToolCtx {
  state: ClusterState;
  shell: LinuxShell;
  /** État des services systemd simulés (kubelet…). true = actif. */
  services: Record<string, boolean>;
  /** Nœud « courant » (après ssh). Par défaut le control-plane. */
  currentNode: () => NodeObj;
}

// Hash déterministe court (id de conteneur/pod crédible et STABLE).
function hexId(seed: string, len = 13): string {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < seed.length; i++) { h ^= seed.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; }
  let out = (h >>> 0).toString(16);
  while (out.length < len) { h = Math.imul(h ^ out.length, 16777619) >>> 0; out += (h >>> 0).toString(16); }
  return out.slice(0, len);
}

function table(rows: string[][]): string {
  const w: number[] = [];
  for (const r of rows) r.forEach((c, i) => { w[i] = Math.max(w[i] || 0, c.length); });
  return rows.map((r) => r.map((c, i) => (i === r.length - 1 ? c : c.padEnd(w[i] + 3))).join('')).join('\n');
}

// ───────────────────────────── crictl ──────────────────────────────────────
function crictl(args: string[], ctx: NodeToolCtx): ExecResultLite {
  const sub = args[0];
  const pods = ctx.state.pods;
  // Chaque conteneur de chaque pod = un conteneur runtime avec un id stable.
  const containers = pods.flatMap((p) =>
    p.containers.map((c) => ({
      id: hexId(`${p.namespace}/${p.name}/${c.name}`),
      podId: hexId(`pod/${p.namespace}/${p.name}`),
      image: c.image,
      imageId: hexId(`img/${c.image}`),
      name: c.name,
      pod: p.name,
      ns: p.namespace,
      state: p.status === 'Running' ? 'Running' : (p.status === 'Completed' ? 'Exited' : 'Created'),
    })),
  );

  if (sub === 'ps') {
    const rows = [['CONTAINER', 'IMAGE', 'CREATED', 'STATE', 'NAME', 'ATTEMPT', 'POD ID', 'POD']];
    containers.forEach((c) => rows.push([c.id, c.imageId, '2 minutes ago', c.state, c.name, '0', c.podId, c.pod]));
    return ok(table(rows));
  }
  if (sub === 'pods') {
    const rows = [['POD ID', 'CREATED', 'STATE', 'NAME', 'NAMESPACE', 'ATTEMPT', 'RUNTIME']];
    pods.forEach((p) => rows.push([hexId(`pod/${p.namespace}/${p.name}`), '2 minutes ago', 'Ready', p.name, p.namespace, '0', '(default)']));
    return ok(table(rows));
  }
  if (sub === 'images') {
    const imgs = [...new Set(containers.map((c) => c.image))];
    const rows = [['IMAGE', 'TAG', 'IMAGE ID', 'SIZE']];
    imgs.forEach((img) => { const [repo, tag = 'latest'] = img.split(':'); rows.push([repo, tag, hexId(`img/${img}`).slice(0, 12), '22MB']); });
    return ok(table(rows));
  }
  if (sub === 'inspect') {
    const id = args[1];
    const c = containers.find((x) => x.id.startsWith(id || '__'));
    if (!c) return err(`FATA[0000] no such container "${id}"`);
    return ok(JSON.stringify({ status: { id: c.id, metadata: { name: c.name }, state: `CONTAINER_${c.state.toUpperCase()}`, image: { image: c.image } }, info: { runtimeType: 'io.containerd.runc.v2', pid: 12345 } }, null, 2));
  }
  if (sub === 'logs') {
    const id = args[args.length - 1];
    const c = containers.find((x) => x.id.startsWith(id || '__'));
    if (!c) return err(`FATA[0000] no such container "${id}"`);
    return ok(`# (logs simulés) conteneur ${c.name} (${c.image}) — sortie non capturée dans ce simulateur.`);
  }
  if (sub === 'rm') {
    const id = args[args.length - 1];
    return ok(id);
  }
  if (sub === 'version') return ok('Version:  0.1.0\nRuntimeName:  containerd\nRuntimeVersion:  1.7.24\nRuntimeApiVersion:  v1');
  if (sub === 'info') return ok('{\n  "status": {\n    "conditions": [ { "type": "RuntimeReady", "status": true } ]\n  }\n}');
  return err(`unknown crictl subcommand "${sub || ''}". Supporté : ps, pods, images, inspect, logs, rm, version, info.`);
}

// ───────────────────────────── kubeadm ─────────────────────────────────────
function kubeadm(args: string[], ctx: NodeToolCtx): ExecResultLite {
  const V = ctx.currentNode().version.replace(/^v/, '');
  const join = () => `kubeadm join 192.168.100.11:6443 --token ${hexId('token', 6)}.${hexId('tokensecret', 16)} --discovery-token-ca-cert-hash sha256:${hexId('cahash', 64)}`;
  if (args[0] === 'version') {
    return ok(`kubeadm version: &version.Info{Major:"1", Minor:"35", GitVersion:"v${V}", GitCommit:"${hexId('commit', 40)}", GitTreeState:"clean", BuildDate:"2026-06-11T18:07:12Z", GoVersion:"go1.25.11", Compiler:"gc", Platform:"linux/amd64"}`);
  }
  if (args[0] === 'token') {
    if (args[1] === 'list') {
      const rows = [['TOKEN', 'TTL', 'EXPIRES', 'USAGES', 'DESCRIPTION']];
      rows.push([`${hexId('token', 6)}.${hexId('tokensecret', 16)}`, '23h', '2026-08-04T15:12:26Z', 'authentication,signing', '<none>']);
      return ok(table(rows));
    }
    if (args[1] === 'create') {
      if (args.includes('--print-join-command')) return ok(join());
      return ok(`${hexId('token', 6)}.${hexId('tokensecret', 16)}`);
    }
  }
  if (args[0] === 'certs' || args[0] === 'alpha') {
    const sub = args[0] === 'alpha' ? args[2] : args[1];
    if (sub === 'check-expiration') {
      const exp = 'Aug 03, 2027 14:19 UTC';
      const rows = [['CERTIFICATE', 'EXPIRES', 'RESIDUAL TIME', 'CERTIFICATE AUTHORITY', 'EXTERNALLY MANAGED']];
      ['admin.conf', 'apiserver', 'apiserver-etcd-client', 'apiserver-kubelet-client', 'controller-manager.conf', 'etcd-healthcheck-client', 'etcd-peer', 'etcd-server', 'front-proxy-client', 'scheduler.conf'].forEach((c) =>
        rows.push([c, exp, '364d', c.includes('etcd') ? 'etcd-ca' : 'ca', 'no']));
      return ok(table(rows) + '\n\nCERTIFICATE AUTHORITY   EXPIRES                  RESIDUAL TIME\nca                      Jul 30, 2035 14:14 UTC   9y\netcd-ca                 Jul 30, 2035 14:14 UTC   9y');
    }
    if (sub === 'renew') {
      const what = args[args.length - 1];
      return ok(`certificate for "${what}" renewed\n\nDone renewing certificates. You must restart the kube-apiserver, kube-controller-manager, kube-scheduler and etcd, so that they can use the new certificates.`);
    }
  }
  if (args[0] === 'upgrade') {
    if (args[1] === 'plan') return ok(`[upgrade/config] Making sure the configuration is correct:\n[upgrade] Fetching available versions to upgrade to\n[upgrade/versions] Cluster version: v${V}\n[upgrade/versions] kubeadm version: v${V}\n\nComponents that must be upgraded manually after you have upgraded the control plane with 'kubeadm upgrade apply':\nCOMPONENT   NODE     CURRENT   TARGET\nkubelet     cka-cp   v${V}    v${V}\n\nUp to date! No upgrade available.`);
    if (args[1] === 'apply') return ok(`[upgrade/successful] SUCCESS! Your cluster was upgraded to "${args[2] || 'v' + V}". Enjoy!\n\n[upgrade/kubelet] Now that your control plane is upgraded, please proceed with upgrading your kubelets if you haven't already done so.`);
    if (args[1] === 'node') return ok('[upgrade] The configuration for this node was successfully updated!\n[upgrade] Now you should go ahead and upgrade the kubelet package using your package manager.');
  }
  if (args[0] === 'join') {
    ctx.services['kubelet'] = true;
    return ok('[preflight] Running pre-flight checks\n[kubelet-start] Starting the kubelet\n[kubelet-check] The kubelet is healthy after 501ms\n\nThis node has joined the cluster:\n* Certificate signing request was sent to apiserver and a response was received.\n* The Kubelet was informed of the new secure connection details.\n\nRun \'kubectl get nodes\' on the control-plane to see this node join the cluster.');
  }
  if (args[0] === 'reset') return ok('[reset] Reading configuration from the cluster...\n[reset] The reset process does not clean CNI configuration. To do so, you must remove /etc/cni/net.d\n\nThe reset process completed successfully!');
  if (args[0] === 'init' && args[1] === 'phase') return ok('[done] phase executed (simulé).');
  return err(`kubeadm: sous-commande "${args.join(' ')}" non gérée. Supporté : version, token, certs, upgrade, join, reset.`);
}

// ───────────────────────────── openssl ─────────────────────────────────────
function openssl(args: string[], ctx: NodeToolCtx): ExecResultLite {
  if (args[0] === 'version') return ok('OpenSSL 3.0.13 30 Jan 2024 (Library: OpenSSL 3.0.13 30 Jan 2024)');
  if (args[0] !== 'x509') return ok(`# openssl ${args.join(' ')} (opération simulée)`);
  const fileIdx = args.indexOf('-in');
  const file = fileIdx >= 0 ? args[fileIdx + 1] : null;
  if (!file) return err('usage: openssl x509 -in <file> [-noout] [-text|-enddate|-subject|-issuer|-dates]');
  const content = ctx.shell.readFile(file);
  if (content === null) return err(`Could not open file or uri for loading certificate input from ${file}\nunable to load certificate`);
  // Émet un observable crédible dérivé du NOM du fichier.
  const base = file.split('/').pop() || file;
  const isClient = /client|kubelet-client/.test(base);
  const isServer = /server|kubelet\.crt|apiserver\.crt/.test(base) && !isClient;
  const issuer = /kubelet\.crt/.test(base) ? `CN = ${ctx.currentNode().name}-ca@1730211854` : (/etcd/.test(base) ? 'CN = etcd-ca' : 'CN = kubernetes');
  const cn = /kubelet/.test(base) ? 'system:node:' + ctx.currentNode().name : (/apiserver\.crt/.test(base) ? 'kube-apiserver' : 'CN=' + base.replace(/\.(crt|pem)$/, ''));
  const eku = isServer ? 'TLS Web Server Authentication' : (isClient ? 'TLS Web Client Authentication' : 'TLS Web Server Authentication, TLS Web Client Authentication');
  const notBefore = 'Jul 30 14:14:27 2026 GMT';
  const notAfter = 'Aug  3 14:19:27 2027 GMT';
  if (args.includes('-enddate')) return ok(`notAfter=${notAfter}`);
  if (args.includes('-startdate')) return ok(`notBefore=${notBefore}`);
  if (args.includes('-dates')) return ok(`notBefore=${notBefore}\nnotAfter=${notAfter}`);
  if (args.includes('-subject')) return ok(`subject=${cn}`);
  if (args.includes('-issuer')) return ok(`issuer=${issuer}`);
  // -text : dump complet (extraits utiles à l'examen).
  return ok(
    `Certificate:\n    Data:\n        Version: 3 (0x2)\n        Serial Number: ${hexId('serial', 16)}\n        Signature Algorithm: sha256WithRSAEncryption\n        Issuer: ${issuer}\n        Validity\n            Not Before: ${notBefore}\n            Not After : ${notAfter}\n        Subject: ${cn}\n        X509v3 extensions:\n            X509v3 Key Usage: critical\n                Digital Signature, Key Encipherment\n            X509v3 Extended Key Usage: \n                ${eku}`,
  );
}

// ─────────────────────── systemctl / service ───────────────────────────────
function serviceCtl(bin: string, args: string[], ctx: NodeToolCtx): ExecResultLite {
  // Formes : `systemctl <action> <unit>` ou `service <unit> <action>`.
  let action: string, unit: string;
  if (bin === 'service') { unit = args[0]; action = args[1]; }
  else { action = args[0]; unit = args[1]; }
  if (action === 'daemon-reload') return ok('');
  unit = (unit || '').replace(/\.service$/, '');
  if (!unit) return err(`usage: ${bin} ...`);
  if (ctx.services[unit] === undefined) ctx.services[unit] = true;
  if (action === 'start' || action === 'restart') { ctx.services[unit] = true; return ok(''); }
  if (action === 'stop') { ctx.services[unit] = false; return ok(''); }
  if (action === 'enable' || action === 'disable') return ok(`Created symlink /etc/systemd/system/multi-user.target.wants/${unit}.service (simulé).`);
  if (action === 'is-active') return ok(ctx.services[unit] ? 'active' : 'inactive');
  if (action === 'status') {
    const active = ctx.services[unit];
    return ok(
      `● ${unit}.service - ${unit === 'kubelet' ? 'kubelet: The Kubernetes Node Agent' : unit}\n` +
      `     Loaded: loaded (/usr/lib/systemd/system/${unit}.service; enabled; preset: enabled)\n` +
      (unit === 'kubelet' ? `    Drop-In: /usr/lib/systemd/system/kubelet.service.d\n             └─10-kubeadm.conf\n` : '') +
      `     Active: ${active ? 'active (running)' : 'inactive (dead)'} since ${active ? 'Mon 2026-08-03 12:33:25 UTC; 5s ago' : 'Sun 2026-08-03 08:16:52 UTC; 4h ago'}\n` +
      `       Docs: https://kubernetes.io/docs/\n` +
      (active ? `   Main PID: 13124 (${unit})\n      Tasks: 11\n     Memory: 88.3M` : `   Main PID: 7346 (code=exited, status=0/SUCCESS)`),
    );
  }
  return err(`${bin}: action "${action}" non gérée. Supporté : status, start, stop, restart, enable, disable, is-active, daemon-reload.`);
}

// ─────────────────────── etcdctl / etcdutl ─────────────────────────────────
function etcd(bin: string, args: string[], ctx: NodeToolCtx): ExecResultLite {
  if (args.includes('--version') || args[0] === 'version') {
    return ok('etcdctl version: 3.6.4\nAPI version: 3.6');
  }
  if (args[0] === 'member' && args[1] === 'list') {
    return ok(`${hexId('member', 16)}, started, cka-cp, https://192.168.100.11:2380, https://192.168.100.11:2379, false`);
  }
  if (args[0] === 'endpoint' && args[1] === 'health') {
    return ok('127.0.0.1:2379 is healthy: successfully committed proposal: took = 3.1ms');
  }
  if (args[0] === 'snapshot' && args[1] === 'save') {
    const file = args[2] || '/tmp/snapshot.db';
    ctx.shell.saveFile(file, `# etcd snapshot (simulé) ${new Date().toISOString()}\n`);
    return ok(`{"level":"info","msg":"saved","path":"${file}"}\nSnapshot saved at ${file}`);
  }
  if (args[0] === 'snapshot' && (args[1] === 'restore' || bin === 'etcdutl')) {
    const file = args[bin === 'etcdutl' ? 2 : 2] || args[args.length - 1];
    return ok(`{"level":"info","msg":"restored snapshot","path":"${file}"}`);
  }
  if (bin === 'etcdutl' && args[0] === 'snapshot' && args[1] === 'restore') {
    return ok(`{"level":"info","msg":"restored snapshot"}`);
  }
  return err(`${bin}: commande non gérée. Supporté : version, member list, endpoint health, snapshot save/restore.`);
}

/**
 * Point d'entrée : si `line` commence par un binaire niveau-nœud connu, exécute
 * et renvoie le résultat ; sinon renvoie null (pour laisser le shell Linux gérer).
 */
export function runNodeTool(line: string, ctx: NodeToolCtx): ExecResultLite | null {
  const tokens = line.trim().split(/\s+/);
  const bin = tokens[0];
  const rest = tokens.slice(1);
  switch (bin) {
    case 'crictl': return crictl(rest, ctx);
    case 'kubeadm': return kubeadm(rest, ctx);
    case 'openssl': return openssl(rest, ctx);
    case 'systemctl': return serviceCtl('systemctl', rest, ctx);
    case 'service': return serviceCtl('service', rest, ctx);
    case 'etcdctl': return etcd('etcdctl', rest, ctx);
    case 'etcdutl': return etcd('etcdutl', rest, ctx);
    default: return null;
  }
}
